import React, { useContext, useState } from 'react';
import ModalModule from '../modal';
import Utils from '../utils';
import Spinner from '../ui/Spinner.component';
import PlanChangedSuccessfulModal from './PlanChangedSuccessfulModal.component';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { Analytics, BillingPlans, Session, Util, Communication } = Utils;
const { prettyPrintPlan } = BillingPlans;

const PlanChangeConfirmationModal = ({ modalId, data }) => {
	const {
		newPlanName,
		newPlanId,
		isUpgrade,
		newPlanPrice,
		onUpgradePlanCallback,
		upgradePrompt = '',
	} = data;
	const { BillingDayOfMonth } = Session.getInternalAccountInfo();
	const { customerDetails = {} } = Session.getAccountInfo();
	const customerEmail = customerDetails?.email;
	const { closeModal, showModal, showError } = useContext(ModalContext);
	const [spinner, setSpinner] = useState(false);

	const { Price = '', PlanId = '' } = BillingPlans.getCurrentPlan();

	const logTrackEvent = confirm => {
		if (upgradePrompt !== '') {
			// EVENT USED BY MARKETING TEAM
			Analytics.trackEvent(
				'changePlan',
				{
					pointOfOrigin: upgradePrompt,
					confirm,
					newPlanId,
					newPlanPrice,
					oldPlanId: PlanId,
					oldPlanPrice: Price,
					email: customerEmail,
				},
				Analytics.agent.segment
			);
		}
	};

	const onChangePlan = async () => {
		const changePlanRequest = {
			PlanId: newPlanId,
			newPlanPrice,
			Disposition: {},
			CancelReason: {},
		};

		setSpinner(true);

		const response = await Communication.changePlan(changePlanRequest);

		if (Communication.isSuccessful(response)) {
			setTimeout(async () => {
				await Session.loadAccountInfo();
				setSpinner(false);
				// close the modal
				closeModal(modalId, ModalButtonActionType.Ok);

				logTrackEvent(true);

				showModal({
					component: PlanChangedSuccessfulModal,
					data: {
						newPlanPrice,
						isUpgrade,
						onUpgradePlanCallback,
						newPlanId,
					},
					closeCallback: () => {
						if (onUpgradePlanCallback) onUpgradePlanCallback(newPlanId);
					},
				});
			}, 7000);
		} else {
			showError({ response });
			logTrackEvent(false);
		}
	};

	const primaryButton = {
		type: ModalButtonActionType.Confirm,
		text: 'Confirm',
		onClick: onChangePlan,
		dataqa: 'plan-change-confirmation-confirm-button',
	};

	const secondaryButton = {
		type: ModalButtonActionType.Cancel,
		text: 'Cancel',
		dataqa: 'plan-change-confirmation-cancel-button',
	};

	const getUpgradeModal = () => (
		<Modal
			modalId={modalId}
			className="subscription-plan plan-change-modal"
			header={`Change Plan to ${prettyPrintPlan(newPlanName)}`}
			primaryButton={primaryButton}
			secondaryButton={secondaryButton}
		>
			<div className="body">
				<p className="section1">
					Once confirmed, you will have access to your new plan features in just a few
					minutes.
				</p>
				<p className="section2">
					Your invoice for this billing cycle on {Util.getBillingDate(BillingDayOfMonth)}{' '}
					will include a prorated amount for your new plan, based on the number of days
					remaining in the billing cycle.
				</p>
			</div>
			{spinner && <Spinner msg="Changing plan..." />}
		</Modal>
	);

	const getDowngradeModal = () => {
		const billingDate = Util.getBillingDate(BillingDayOfMonth);
		const nextDayBillingDate = Util.getNextDay(billingDate);

		return (
			<Modal
				modalId={modalId}
				className="subscription-plan plan-change-modal"
				header={`Change Plan to ${prettyPrintPlan(newPlanName)}`}
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
			>
				<div className="body">
					<p className="section1">
						Once confirmed, your new plan will start on the first day of your next
						billing cycle {nextDayBillingDate}.
					</p>
					<p className="section2">
						Your account will remain on your prior plan, with the same features and plan
						fee until this billing cycle ends on {billingDate}.
					</p>
				</div>
				{spinner && <Spinner msg="Changing plan..." />}
			</Modal>
		);
	};

	const getPlanChangeModal = () => {
		if (isUpgrade) {
			return getUpgradeModal();
		} else {
			return getDowngradeModal();
		}
	};

	return getPlanChangeModal();
};

export default PlanChangeConfirmationModal;
